import { Link, useRouterState } from "@tanstack/react-router";
import {
	Home,
	Building2,
	UserPlus,
	ClipboardList,
	FileText,
	ScrollText,
	ShoppingCart,
	Warehouse,
	PackageSearch,
	Truck,
	Tags,
	LogOut,
	RefreshCw,
	Menu,
	X,
	Settings,
	Terminal,
	Bot,
	Users,
	UserCircle,
	Cog,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { useState, useEffect } from "react";
import { ThemeToggle } from "../ThemeToggle/ThemeToggle";
import { LanguageSwitcher } from "../LanguageSwitcher/LanguageSwitcher";
import { Avatar } from "../ui/Avatar/Avatar";
import { NotificationBell } from "../ui/NotificationBell/NotificationBell";
import { useAuth } from "../../lib/use-auth";
import { usePublicSettings } from "../../hooks/usePublicSettings";
import { hasPermission } from "../../lib/permissions";
import type { PermissionAction } from "../../lib/permissions";
import styles from "./Layout.module.css";

interface NavItem {
	to: string;
	label: string;
	icon: React.FC<{ size?: number }>;
	module?: string;
	action?: PermissionAction;
}

const navItems: NavItem[] = [
	{ to: "/", label: "home", icon: Home },
	{ to: "/account", label: "myAccount", icon: UserCircle },
	{ to: "/companies", label: "companies", icon: Building2, module: "companies", action: "read" },
	{ to: "/leads", label: "leads", icon: UserPlus, module: "leads", action: "read" },
	{
		to: "/activities",
		label: "activities",
		icon: ClipboardList,
		module: "activities",
		action: "read",
	},
	{ to: "/offers", label: "offers", icon: FileText, module: "offers", action: "read" },
	{ to: "/orders", label: "orders", icon: ShoppingCart, module: "orders", action: "read" },
	{ to: "/contracts", label: "contracts", icon: ScrollText, module: "contracts", action: "read" },
	{ to: "/warehouses", label: "warehouses", icon: Warehouse, module: "warehouses", action: "read" },
	{
		to: "/stock-items",
		label: "stockItems",
		icon: PackageSearch,
		module: "stock_items",
		action: "read",
	},
	{ to: "/load-reports", label: "loadReports", icon: Truck },
	{ to: "/price-list", label: "priceList", icon: Tags },
	{ to: "/chat", label: "aiChat", icon: Bot, module: "ai", action: "read" },
];

export function Layout({ children }: { children: React.ReactNode }) {
	const { user, logout } = useAuth();
	const { t } = useTranslation();
	const { appName, aiBotName } = usePublicSettings();
	const { location } = useRouterState();
	const [drawerOpen, setDrawerOpen] = useState(false);
	const currentPath = location.pathname;

	useEffect(() => {
		if (!drawerOpen) return;
		const original = document.body.style.overflow;
		document.body.style.overflow = "hidden";
		return () => {
			document.body.style.overflow = original;
		};
	}, [drawerOpen]);

	const isActive = (path: string) => currentPath === path || currentPath.startsWith(`${path}/`);

	const visibleNavItems = navItems.filter((item) => {
		if (!item.module || !item.action) return true;
		return hasPermission(user, item.module, item.action);
	});

	return (
		<div className={styles.layout}>
			<aside className={styles.sidebar}>
				<div className={styles.sidebarHeader}>
					<div className={styles.sidebarHeaderRow}>
						<Link to="/" className={styles.logo}>
							{appName}
						</Link>
						<div className={styles.headerUserArea}>
							<Link to="/account" title={t("myAccount")}>
								<Avatar
									firstName={user?.firstName}
									lastName={user?.lastName}
									email={user?.email}
									size={32}
								/>
							</Link>
							<NotificationBell />
						</div>
					</div>
				</div>
				<nav className={styles.sidebarNav}>
					{visibleNavItems.map((item) => (
						<Link
							key={item.to}
							to={item.to}
							className={[styles.sidebarLink, isActive(item.to) && styles.active]
								.filter(Boolean)
								.join(" ")}
						>
							<item.icon size={18} />
							<span>
								{item.label === "aiChat" ? t("aiChat", { botName: aiBotName }) : t(item.label)}
							</span>
						</Link>
					))}
					{user?.role === "admin" && (
						<>
							<Link
								to="/sync"
								className={[styles.sidebarLink, isActive("/sync") && styles.active]
									.filter(Boolean)
									.join(" ")}
							>
								<RefreshCw size={18} />
								<span>{t("sync")}</span>
							</Link>
							<Link
								to="/admin/settings"
								className={[styles.sidebarLink, isActive("/admin/settings") && styles.active]
									.filter(Boolean)
									.join(" ")}
							>
								<Settings size={18} />
								<span>{t("settings")}</span>
							</Link>
							<Link
								to="/admin/users"
								className={[styles.sidebarLink, isActive("/admin/users") && styles.active]
									.filter(Boolean)
									.join(" ")}
							>
								<Users size={18} />
								<span>{t("users")}</span>
							</Link>
							<Link
								to="/admin/engine-brands"
								className={[styles.sidebarLink, isActive("/admin/engine-brands") && styles.active]
									.filter(Boolean)
									.join(" ")}
							>
								<Cog size={18} />
								<span>{t("engineBrands")}</span>
							</Link>
							<Link
								to="/admin/logs"
								search={{ page: 1, size: 20 }}
								className={[styles.sidebarLink, isActive("/admin/logs") && styles.active]
									.filter(Boolean)
									.join(" ")}
							>
								<Terminal size={18} />
								<span>{t("logs")}</span>
							</Link>
						</>
					)}
				</nav>
				<div className={styles.sidebarFooter}>
					<div className={styles.sidebarControls}>
						<ThemeToggle />
						<LanguageSwitcher />
					</div>
					<button
						type="button"
						className={styles.logoutButton}
						onClick={logout}
						title={t("logout")}
					>
						<LogOut size={16} />
						<span>{t("logout")}</span>
					</button>
				</div>
			</aside>

			<header className={styles.mobileHeader}>
				<Link to="/" className={styles.logo}>
					{appName}
				</Link>
				<div className={styles.mobileActions}>
					<NotificationBell />
					<LanguageSwitcher />
					<ThemeToggle />
					<button
						type="button"
						className={styles.drawerToggle}
						onClick={() => setDrawerOpen(true)}
						aria-label={t("menu")}
					>
						<Menu size={22} />
					</button>
				</div>
			</header>

			{drawerOpen && (
				<div className={styles.drawerOverlay} onClick={() => setDrawerOpen(false)}>
					<div className={styles.drawer} onClick={(e) => e.stopPropagation()}>
						<div className={styles.drawerHeader}>
							<span className={styles.logo}>{appName}</span>
							<button
								type="button"
								className={styles.drawerClose}
								onClick={() => setDrawerOpen(false)}
								aria-label={t("close")}
							>
								<X size={22} />
							</button>
						</div>
						<nav className={styles.drawerNav}>
							{visibleNavItems.map((item) => (
								<Link
									key={item.to}
									to={item.to}
									className={[styles.drawerLink, isActive(item.to) && styles.active]
										.filter(Boolean)
										.join(" ")}
									onClick={() => setDrawerOpen(false)}
								>
									<item.icon size={20} />
									<span>
										{item.label === "aiChat" ? t("aiChat", { botName: aiBotName }) : t(item.label)}
									</span>
								</Link>
							))}
							{user?.role === "admin" && (
								<>
									<Link
										to="/sync"
										className={[styles.drawerLink, isActive("/sync") && styles.active]
											.filter(Boolean)
											.join(" ")}
										onClick={() => setDrawerOpen(false)}
									>
										<RefreshCw size={20} />
										<span>{t("sync")}</span>
									</Link>
									<Link
										to="/admin/settings"
										className={[styles.drawerLink, isActive("/admin/settings") && styles.active]
											.filter(Boolean)
											.join(" ")}
										onClick={() => setDrawerOpen(false)}
									>
										<Settings size={20} />
										<span>{t("settings")}</span>
									</Link>
									<Link
										to="/admin/users"
										className={[styles.drawerLink, isActive("/admin/users") && styles.active]
											.filter(Boolean)
											.join(" ")}
										onClick={() => setDrawerOpen(false)}
									>
										<Users size={20} />
										<span>{t("users")}</span>
									</Link>
									<Link
										to="/admin/engine-brands"
										className={[
											styles.drawerLink,
											isActive("/admin/engine-brands") && styles.active,
										]
											.filter(Boolean)
											.join(" ")}
										onClick={() => setDrawerOpen(false)}
									>
										<Cog size={20} />
										<span>{t("engineBrands")}</span>
									</Link>
								</>
							)}
							<button type="button" className={styles.drawerLink} onClick={logout}>
								<LogOut size={20} />
								<span>{t("logout")}</span>
							</button>
						</nav>
					</div>
				</div>
			)}

			<main className={styles.content}>{children}</main>

			<nav className={styles.bottomNav}>
				<ul className={styles.bottomNavList}>
					<li>
						<Link
							to="/"
							className={[styles.bottomNavLink, isActive("/") && styles.active]
								.filter(Boolean)
								.join(" ")}
						>
							<Home />
							<span>{t("home")}</span>
						</Link>
					</li>
					<li>
						<Link
							to="/companies"
							search={{
								page: 1,
								size: 20,
								status: "",
								search: "",
								createdAtFrom: "",
								createdAtTo: "",
							}}
							className={[styles.bottomNavLink, isActive("/companies") && styles.active]
								.filter(Boolean)
								.join(" ")}
						>
							<Building2 />
							<span>{t("companies")}</span>
						</Link>
					</li>
					<li>
						<Link
							to="/leads"
							search={{
								page: 1,
								size: 20,
								status: "",
								search: "",
								createdAtFrom: "",
								createdAtTo: "",
							}}
							className={[styles.bottomNavLink, isActive("/leads") && styles.active]
								.filter(Boolean)
								.join(" ")}
						>
							<UserPlus />
							<span>{t("leads")}</span>
						</Link>
					</li>
					<li>
						<button
							type="button"
							className={styles.bottomNavLink}
							onClick={() => setDrawerOpen(true)}
						>
							<Menu />
							<span>{t("more")}</span>
						</button>
					</li>
				</ul>
			</nav>
		</div>
	);
}
